import React from 'react';
import { Layout, LayoutContent } from 'platform/components/layout/Layout';
import { SellerIndependentPageHeader } from 'sellDirect/components/offer/SellerHeader';
import {
  Card,
  Flex,
  Heading,
  Text,
  List,
  Icon,
  AccordionListItem,
  AccordionListItemProps,
  InputProps,
  FullScreenLoader,
} from '@opendoor/bricks/core';
import { useSSEAContext } from 'sellDirect/SSEAContext';
import { useExperimentationContext } from 'sellDirect/ExperimentationContext';
import { useOfferContext, isPreClosingOffer, PreClosingOffer } from 'sellDirect/OfferContext';
import { useUpdateLeadPhoneNumber } from 'sellDirect/graphql/useUpdateLeadPhoneNumber';
import { useObservability } from 'shared/helpers/observability';
import { Redirect } from 'platform/react-router';
import { getHubSellingRoute } from 'shared/helpers/routes';
import { ScheduleEaCard } from 'sellDirect/routes/exterior-assessment/components/ScheduleEaCard';
import { CONTACT_PHONE } from 'shared/helpers/globals';

const MAX_LAYOUT_WIDTH_DESKTOP = '636px';

const EXPECTATION_LIST = [
  {
    title: 'Typically 30 minutes or less',
    body: 'We’ll examine your home’s exterior and take a quick look inside.',
  },
  {
    title: 'No preparation necessary',
    body: 'Just plan to be at home during your scheduled time.',
  },
  {
    title: 'Required for your final offer',
    body: 'This is the last bit of info we need to prepare your final offer. ',
  },
];

const FAQ_LIST: Array<AccordionListItemProps> = [
  {
    panelId: 'what-we-look-for',
    title: 'What are you looking for during the assessment?',
    children:
      'We’re reviewing the home to ensure that major areas are safe, functional, and in good repair.',
  },
  {
    panelId: 'covid-19',
    title: 'What precautions are you taking due to COVID-19?',
    children:
      'The Opendoor team member who visits your home will bring a face mask and practice safe social distancing.',
  },
  {
    panelId: 'be-at-home',
    title: 'Do I need to be home for this?',
    // TODO: This is a placeholder. Update this phone number.
    children: `You or someone over the age of 18 will need to be home. If you can provide another way for us to access the home, reach out to your Opendoor Expert at ${CONTACT_PHONE}`,
  },
  {
    panelId: 'final-offer',
    title: 'When will I get my final offer?',
    children:
      'Once we’re done with the home assessment, it typically takes a few days to email your final offer. You’ll be able to see your final offer price along with the service charge, any repair cost, and your estimated closing costs.',
  },
];

/** Form state for the Schedule EA form */
export type ScheduleEaFormState = {
  phoneNumber: string | undefined;
  isPhoneNumberValid: boolean;
};
const DEFAULT_FORM_STATE: ScheduleEaFormState = {
  phoneNumber: undefined,
  isPhoneNumberValid: false,
};

interface ExteriorAssessmentPageWithoutContextProps {
  /** Determines if the In Person SSEA experiment is enabled.  */
  isInPersonSseaEnabled: boolean;
  /** SSEA Context */
  sseaContext: ReturnType<typeof useSSEAContext>;
  /** Prelim offer */
  offer: PreClosingOffer;
  /** If we should show the agent experience */
  shouldSeeAgentExperience: boolean;
  /** Callback to use to update the lead phone number */
  updateLeadCustomerPhoneNumber: ReturnType<
    typeof useUpdateLeadPhoneNumber
  >['updateLeadCustomerPhoneNumber'];
}

const ExteriorAssessmentPageWithoutContext: React.FC<ExteriorAssessmentPageWithoutContextProps> = ({
  sseaContext,
  isInPersonSseaEnabled,
  updateLeadCustomerPhoneNumber,
}) => {
  const [formState, setFormState] = React.useState<ScheduleEaFormState>(DEFAULT_FORM_STATE);

  const onPhoneNumberChange: InputProps['onChange'] = (
    _event,
    phoneNumber,
    isPhoneNumberValid = false,
  ) => {
    _event?.preventDefault();
    setFormState({
      phoneNumber,
      isPhoneNumberValid,
    });
  };

  return (
    <Layout
      hideNav
      renderHeader={() => <SellerIndependentPageHeader title="Home assessment" />}
      hideFooter
    >
      <LayoutContent
        maxWidth="100%"
        px={[6, 6, 3]}
        maxW={MAX_LAYOUT_WIDTH_DESKTOP}
        alignItems="flex-start"
        gridGap="5"
        pt={[10, 10, 10]}
        data-testid="exterior-assessment-in-hub"
      >
        <Flex mb="5" flexDir="column" gridGap="5">
          <Heading
            as="h2"
            fontSize={['s4']}
            fontWeight="semibold"
            lineHeight={['s4']}
            textAlign="start"
          >
            Schedule your home assessment
          </Heading>
          <Text>
            This is the last step before your final offer. Find a time that works for you and a team
            member will stop by. They’ll check out your home’s exterior and take a quick look
            inside.
          </Text>
        </Flex>

        {/* Card */}
        <ScheduleEaCard
          formState={formState}
          onPhoneNumberChange={onPhoneNumberChange}
          sseaContext={sseaContext}
          isInPersonSseaEnabled={isInPersonSseaEnabled}
          updateLeadCustomerPhoneNumber={updateLeadCustomerPhoneNumber}
        />

        {/* What to expect */}
        <Card variant="tertiary" gridGap="8">
          <Heading as="h2" fontSize="s3" lineHeight="s3" fontWeight="semibold">
            What to expect for your home assessment
          </Heading>
          <List variant="accordion">
            {EXPECTATION_LIST.map((expectation) => (
              <ExpectationListItem {...expectation} key={expectation.title} />
            ))}
          </List>
        </Card>

        <Flex flexDir="column" gridGap="6" mt="8">
          <Heading as="h2" fontSize="s2" lineHeight="s2">
            Frequently asked questions
          </Heading>

          <List spacing="10px" variant="accordion" boxShadow="none">
            {FAQ_LIST.map((faq) => (
              <AccordionListItem {...faq} key={faq.panelId} />
            ))}
          </List>
        </Flex>

        {/* FAQs */}
      </LayoutContent>
    </Layout>
  );
};

interface ExpectationListItemProps {
  title: string;
  body: string;
}

const ExpectationListItem: React.FC<ExpectationListItemProps> = ({ title, body }) => (
  <Flex flexDir="row" gridGap="5">
    <Flex
      borderRadius="rounded"
      bgColor="brand50"
      alignItems="center"
      justifyContent="center"
      w="24px"
      minW="24px"
      h="24px"
      minH="24px"
    >
      <Icon name="checkmark" color="neutrals0" size={16} />
    </Flex>
    <Flex flexDir="column">
      <Text fontWeight="semibold">{title}</Text>
      <Text>{body}</Text>
    </Flex>
  </Flex>
);

/**
 * Entry point for the Exterior Assessment Scheduling page.
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const ExteriorAssessmentPage: React.FC = () => {
  const { trackError } = useObservability();
  const { offer, shouldSeeAgentExperience, loading } = useOfferContext();
  const sseaContext = useSSEAContext();
  const { updateLeadCustomerPhoneNumber } = useUpdateLeadPhoneNumber();
  const {
    assertions: { isInPersonSseaEnabled },
  } = useExperimentationContext();

  if (loading) {
    return <FullScreenLoader />;
  }

  if (!isPreClosingOffer(offer)) {
    trackError('cake-exterior-assessment--non-preclosing-offer', undefined, { offerId: offer?.id });
    return <Redirect to={getHubSellingRoute()} />;
  }

  return (
    <ExteriorAssessmentPageWithoutContext
      offer={offer}
      sseaContext={sseaContext}
      isInPersonSseaEnabled={isInPersonSseaEnabled}
      shouldSeeAgentExperience={shouldSeeAgentExperience}
      updateLeadCustomerPhoneNumber={updateLeadCustomerPhoneNumber}
    />
  );
};
